import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Gauge, 
  SlidersHorizontal, 
  Inbox, 
  Briefcase, 
  ClipboardCheck, 
  Handshake, 
  ShieldAlert, 
  Bot, 
  Users, 
  Brain, 
  Wrench, 
  TrendingUp, 
  Book, 
  Database, 
  Server, 
  Code, 
  Network, 
  Lock, 
  Eye, 
  Gavel, 
  MessageSquare, 
  BarChart3, 
  Settings, 
  Plus, 
  Workflow,
  Minus,
  CircleX,
  X as CloseIcon,
  Mail 
} from 'lucide-react';
import { getUserData } from '../../utils/authUtils';

const SidebarContainer = styled(motion.div)`
  width: ${props => props.collapsed ? '0' : '300px'};
  height: calc(100vh - 62px);
  // background: linear-gradient(180deg, var(--neutral-50) 0%, var(--neutral-100) 100%);
  background: #fff;
  border-right: ${props => props.collapsed ? 'none' : '1px solid #e3e3e3'};
  padding: 0;
  padding-top: 10px;
  display: ${props => props.collapsed ? 'none' : 'flex'};
  flex-direction: column;
  position: fixed;
  top: 62px;
  left: 0;
  overflow: hidden;
  z-index: 1000;
  // box-shadow: ${props => props.collapsed ? 'none' : '2px 0 12px rgba(0, 102, 255, 0.08)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  ${props => !props.collapsed && `
    overflow-y: auto;
    overflow-x: hidden;
    
    &::-webkit-scrollbar {
      width: 5px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, rgba(0, 102, 255, 0.2), rgba(0, 163, 255, 0.2));
      border-radius: 10px;
      
      &:hover {
        // background: linear-gradient(180deg, rgba(0, 102, 255, 0.35), rgba(0, 163, 255, 0.35));
      }
    }
  `}
`;

const BrandSection = styled.div`
  padding: 0 ${props => props.collapsed ? '0.5rem' : '1.2rem'};
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  gap: ${props => props.collapsed ? '0' : '0.75rem'};
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(250, 251, 255, 0.9));
  backdrop-filter: blur(10px);
  // border-bottom: 1px solid #e5e9f2;
  position: relative;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left:4px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    // background: linear-gradient(90deg, transparent, rgba(0, 102, 255, 0.4), rgba(124, 58, 237, 0.3), transparent);
  }
`;

const BrandIcon = styled.img`
  height: 40px;
  width: 40px;
  display: block;
  // object-fit: contain;
  // flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 6px rgba(0, 102, 255, 0.15));
      margin-left: 8px;
  
  &:hover {
    // transform: scale(1.08) rotate(-2deg);
    // filter: drop-shadow(0 4px 10px rgba(0, 102, 255, 0.25));
  }
`;

const BrandTextContainer = styled.div`
  display: ${props => props.collapsed ? 'none' : 'flex'};
  flex-direction: column;
  gap: 0.15rem;
  overflow: hidden;
`;

const BrandName = styled.div`
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, var(--primary-600), var(--accent-500));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  transition: all 0.3s ease;
  
  ${BrandSection}:hover & {
    background: linear-gradient(135deg, var(--primary-600), var(--accent-600));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const BrandTagline = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  letter-spacing: 0.5px;
  color: var(--neutral-500);
  text-transform: uppercase;
  line-height: 1.2;
  transition: color 0.3s ease;
  
  ${BrandSection}:hover & {
    color: var(--primary-600);
  }
`;

const MenuSection = styled.div`
  padding: 0.4rem ${props => props.collapsed ? '0.4rem' : '0.8rem'};
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;


const NavGroup = styled.div`
  margin-bottom: 0.8rem;
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: -0.4rem;
    left: ${props => props.collapsed ? '8px' : '0.4rem'};
    right: ${props => props.collapsed ? '8px' : '0.4rem'};
    height: 1px;
    // background: linear-gradient(90deg, transparent, rgba(0, 102, 255, 0.12), rgba(124, 58, 237, 0.08), transparent);
  }
`;

const NavGroupTitle = styled.div`
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--neutral-500);
  margin-bottom: 0.4rem;
  padding: 0.35rem 0.5rem;
  font-weight: var(--font-semibold);
  display: ${props => props.collapsed ? 'none' : 'flex'};
  align-items: center;
  gap: 0.4rem;
  // background: linear-gradient(135deg, rgba(0, 102, 255, 0.06), rgba(124, 58, 237, 0.04));
  border-radius: 6px;
  position: relative;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    width: 3px;
    height: 100%;
    // background: linear-gradient(180deg, var(--primary-600), var(--accent-500));
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 0 3px 3px 0;
  }
`;

const NavItem = styled(motion.div)`
  padding: 0.5rem ${props => props.collapsed ? '0' : '0.6rem'};
  // border-radius: 8px;
  margin-bottom: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  color: ${props => props.$active ? 'var(--primary-600)' : 'var(--neutral-600)'};
  // background: ${props => props.$active ? 'linear-gradient(135deg, rgba(0, 102, 255, 0.12), rgba(124, 58, 237, 0.08))' : 'transparent'};
  border-left: ${props => !props.collapsed && props.$active ? '3px solid var(--primary-600)' : '3px solid transparent'};
  font-weight: ${props => props.$active ? 'var(--font-medium)' : 'var(--font-normal)'};
  font-size: var(--text-sm);
  cursor: pointer;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  // box-shadow: ${props => props.$active ? '0 2px 8px rgba(0, 102, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)' : 'none'};
  backdrop-filter: ${props => props.$active ? 'blur(10px)' : 'none'};
  
  &:hover {
    // background: ${props => props.$active ? 'linear-gradient(135deg, rgba(0, 102, 255, 0.15), rgba(124, 58, 237, 0.1))' : 'linear-gradient(135deg, rgba(0, 102, 255, 0.08), rgba(124, 58, 237, 0.05))'};
    color: var(--primary-600);
    // transform: ${props => props.collapsed ? 'scale(1.08)' : 'translateX(3px)'};
    // box-shadow: 0 4px 14px rgba(0, 102, 255, 0.15);
    // backdrop-filter: blur(10px);
  }
  
  &:active {
    transform: scale(0.97);
  }
`;

const SubNavItem = styled(NavItem)`
  overflow-x:hidden;
  padding-left: ${props => props.collapsed ? '0' : '1rem'};
  font-size: var(--text-sm);
  margin-bottom: 0.15rem;
  margin-left: ${props => props.collapsed ? '0' : '0.3rem'};
  padding-top: 0.45rem;
  padding-bottom: 0.45rem;
  border-left: ${props => !props.collapsed && props.$active ? '2px solid var(--primary-600)' : '2px solid transparent'};
  
  // &::before {
  //   content: '';
  //   position: absolute;
  //   left: ${props => props.collapsed ? '0' : '1.3rem'};
  //   width: 5px;
  //   height: 5px;
  //   background: ${props => props.$active ? 'linear-gradient(135deg, var(--primary-600), var(--accent-500))' : 'var(--neutral-300)'};
  //   border-radius: 50%;
  //   transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  //   display: ${props => props.collapsed ? 'none' : 'block'};
  //   // box-shadow: ${props => props.$active ? '0 0 6px rgba(0, 102, 255, 0.5)' : 'none'};
  // }
  
  // &:hover::before {
  //   background: linear-gradient(135deg, var(--primary-600), var(--accent-500));
  //   // transform: scale(1.6);
  //   // box-shadow: 0 0 8px rgba(0, 102, 255, 0.6);
  // }
`;

const NavIcon = styled.div`
  width: 26px;
  height: 26px;
  min-width: 26px;
  margin-right: ${props => props.collapsed ? '0' : '15px'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: ${props => props.$active ? 'var(--primary-600)' : 'var(--neutral-500)'};
  // background: ${props => props.$active ? 'linear-gradient(135deg, rgba(0, 102, 255, 0.15), rgba(124, 58, 237, 0.1))' : 'rgba(0, 102, 255, 0.05)'};
  border-radius: 7px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  ${NavItem}:hover & {
    color: var(--primary-600);
    // background: linear-gradient(135deg, rgba(0, 102, 255, 0.18), rgba(124, 58, 237, 0.12));
    // transform: scale(1.1) rotate(${props => props.$rotation || '0deg'});
    // box-shadow: 0 4px 12px rgba(0, 102, 255, 0.2);
  }
  
  ${SubNavItem} & {
    width: 20px;
    height: 20px;
    min-width: 20px;
    font-size: var(--text-sm);
    // background: ${props => props.$active ? 'linear-gradient(135deg, rgba(0, 102, 255, 0.12), rgba(124, 58, 237, 0.08))' : 'rgba(0, 102, 255, 0.04)'};
    border-radius: 5px;
  }
  
  ${SubNavItem}:hover & {
    color: var(--primary-600);
    // background: linear-gradient(135deg, rgba(0, 102, 255, 0.15), rgba(124, 58, 237, 0.1));
    // transform: scale(1.15) rotate(${props => props.$rotation || '0deg'});
  }
`;

const NavText = styled.div`
  font-size: inherit;
  display: ${props => props.collapsed ? 'none' : 'block'};
  letter-spacing: 0.01em;
  transition: all 0.3s ease;
  font-weight: inherit;
  line-height: 1.4;
  
  ${NavItem}:hover & {
    // letter-spacing: 0.02em;
  }
`;

const GroupToggle = styled.div`
  margin-left: auto;
  font-size: 0.85rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  // transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: ${props => props.collapsed ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  // background: rgba(0, 102, 255, 0.04);
  
  &:hover {
    // background: linear-gradient(135deg, rgba(0, 102, 255, 0.12), rgba(124, 58, 237, 0.08));
    color: #0066FF;
    // transform: ${props => props.expanded ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1.1)'};
    // box-shadow: 0 2px 8px rgba(0, 102, 255, 0.15);
  }
`;

const SettingsFooter = styled.div`
  padding: 0 ${props => props.collapsed ? '0.5rem' : '1rem'};
  height: 51px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  background: linear-gradient(135deg, rgba(0, 102, 255, 0.04), rgba(124, 58, 237, 0.02));
  border-top: 1px solid #e5e9f2;
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 102, 255, 0.3), rgba(124, 58, 237, 0.2), transparent);
  }
  
  &:hover {
    // background: linear-gradient(135deg, rgba(0, 102, 255, 0.08), rgba(124, 58, 237, 0.05));
  }
`;

const SettingsContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SettingsIcon = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: var(--neutral-500);
  background: rgba(0, 102, 255, 0.08);
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${SettingsFooter}:hover & {
    color: var(--primary-600);
    // background: linear-gradient(135deg, rgba(0, 102, 255, 0.15), rgba(124, 58, 237, 0.1));
    // transform: rotate(90deg);
  }
`;

const SettingsLabel = styled.div`
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--neutral-600);
  display: ${props => props.collapsed ? 'none' : 'block'};
  
  ${SettingsFooter}:hover & {
    color: var(--primary-600);
  }
`;

const Sidebar = ({ collapsed = true, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    dashboard: true,
    agentWorkspace: false,
    agentEcosystem: false,
    knowledge: true,
    governance: false,
    evalWorkspace: false,
    system: false
  });
  const user = getUserData();
  
  const toggleGroup = (group) => {
    setExpandedGroups({
      ...expandedGroups,
      [group]: !expandedGroups[group]
    });
  };
  const basePath = process.env.PUBLIC_URL || "";
  
  const isActive = (path) => {
    // Decode the current pathname to handle URL-encoded characters (like spaces)
    const decodedPathname = decodeURIComponent(location.pathname);
    const decodedPath = decodeURIComponent(path);
    return decodedPathname === decodedPath;
  };

  const hasRoleAccess = (menuRoles) => {
    return menuRoles?.some(role => user?.roles?.includes(role));
  }

  const menuAccess = {
    "dashboard" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_DESIGNER'],
    "mission-control" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "Inbox" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','CLAIM_ASSESSOR','ROLE_ASSESSOR','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "new-business-hub" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "claims-hub" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "services" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "fraud-vault" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "agent-studio-ai" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "personas" :['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "model-hub" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "tools" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "performance" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "pipeline-planner" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "knowledge-base" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "audit-logs": ['ROLE_ADMIN', 'ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "entity" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "data-sources" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "prompt-library" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "email-templates" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "agent-pipelines" :['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "security-governance" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "observability" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "observability-v2" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "policies" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "feedback-loops" :['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "analytics" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "eval-packs" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER'],
    "eval-reports" : ['ROLE_ADMIN','ROLE_COMPANY_ADMIN','ROLE_ANALYTICS','ROLE_DESIGNER']
  }
  
  const mainNavItems = [
    // { id: 'dashboard', icon: Gauge, label: 'Agentic Dashboard', path: '/dashboard', rotation: '15deg' },
    // { id: 'mission-control', icon: SlidersHorizontal, label: 'Mission Control', path: '/mission-control', rotation: '-5deg' },
    { id: 'Inbox', icon: Inbox, label: 'Inbox', path: '/workbench-listing', rotation: '0deg' },
  ];
  
  const agentWorkspaceItems = [
    { id: 'new-business-hub', icon: Briefcase, label: 'New Business', path: '/workstream-hub/New Business', rotation: '5deg' },
    { id: 'claims-hub', icon: ClipboardCheck, label: 'Claims', path: '/workstream-hub/Claims Processing', rotation: '-3deg' },
    { id: 'services', icon: Handshake, label: 'Servicing', path: '/workstream-hub/Servicing', rotation: '8deg' },
    // // { id: 'document-queue', icon: LayerGroup, label: 'Document', path: '/document-queue' },
    { id: 'fraud-vault', icon: ShieldAlert, label: 'FWA', path: '/workstream-hub/Fraud waste and abuse', rotation: '0deg' }
  ];
  
  const agentEcosystemItems = [
    { id: 'agent-studio-ai', icon: Bot, label: 'Agent Studio', path: '/agent-studio-ai', rotation: '10deg' },
    { id: 'personas', icon: Users, label: 'Personas', path: '/personas', rotation: '-5deg' },
    { id: 'model-hub', icon: Brain, label: 'Model Hub', path: '/model-hub', rotation: '8deg' },
    { id: 'tools', icon: Wrench, label: 'Tools', path: '/tools', rotation: '90deg' },
    { id: 'performance', icon: TrendingUp, label: 'Performance', path: '/performance', rotation: '5deg' },
    { id: 'pipeline-planner', icon: Workflow, label: 'Planner', path: '/planner', rotation: '0deg' },
  ];
  
  const knowledgeItems = [
    { id: 'knowledge-base', icon: Book, label: 'Knowledge Base', path: '/knowledge-base', rotation: '-5deg' },
    { id: 'entity', icon: Database, label: 'Entity', path: '/entity', rotation: '0deg' },
    { id: 'data-sources', icon: Server, label: 'Data Sources', path: '/data-sources', rotation: '5deg' },
    { id: 'prompt-library', icon: Code, label: 'Prompt Library', path: '/prompt-library', rotation: '-3deg' },
    { id: 'email-templates', icon: Mail, label: 'Templates', path: '/email-templates', rotation: '-3deg' },
    { id: 'agent-pipelines', icon: Network, label: 'Agent Pipelines', path: '/agent-pipelines', rotation: '8deg' },
  ];
  
  const governanceItems = [
    { id: 'security-governance', icon: Lock, label: 'Security & Governance', path: '/security-governance', rotation: '10deg' },
    { id: 'observability', icon: Eye, label: 'Observability', path: '/observability', rotation: '0deg' },
    { id: 'audit-logs', icon: Eye, label: 'Audit Logs', path: '/audit-logs', rotation: '0deg' },
    { id: 'policies', icon: Gavel, label: 'Policies', path: '/policies', rotation: '-8deg' },
    { id: 'feedback-loops', icon: MessageSquare, label: 'Feedback Loops', path: '/feedback-loops', rotation: '5deg' },
  ];

  const evalItems = [
    { id: 'eval-packs', icon: ClipboardCheck, label: 'Evaluation Packs', path: '/eval-packs', rotation: '0deg' },
    { id: 'eval-reports', icon: BarChart3, label: 'Reports', path: '/eval-reports', rotation: '0deg' },
  ];
  
  const systemItems = [
    { id: 'analytics', icon: BarChart3, label: 'Analytics', path: '/analytics', rotation: '5deg' },
  ];
  
  const renderNavItems = (items, isSubMenu = false) => {
    return items
    .filter(item => hasRoleAccess(menuAccess[item.id])).map(item => {

      return <Link to={item.path} key={item.id} style={{ textDecoration: 'none' }} onClick={onClose}>
        {isSubMenu ? (
          <SubNavItem
            $active={isActive(item.path)}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            whileHover={{ x: collapsed ? 0 : 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
            collapsed={collapsed}
          >
            <NavIcon $active={isActive(item.path)} collapsed={collapsed} $rotation={item.rotation}>
              {typeof item.icon === 'string' ? item.icon : <item.icon size={20} strokeWidth={2} />}
            </NavIcon>
            <NavText collapsed={collapsed}>{item.label}</NavText>
          </SubNavItem>
        ) : (
          <NavItem
            $active={isActive(item.path)}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            whileHover={{ x: collapsed ? 0 : 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
            collapsed={collapsed}
          >
            <NavIcon $active={isActive(item.path)} collapsed={collapsed} $rotation={item.rotation}>
              {typeof item.icon === 'string' ? item.icon : <item.icon size={20} strokeWidth={2} />}
            </NavIcon>
            <NavText collapsed={collapsed}>{item.label}</NavText>
          </NavItem>
        )}
      </Link> 
  }
  );
  };
  
  const renderGroupWithToggle = (title, items, groupKey) => {

  const accessibleItems = items.filter(item =>
    hasRoleAccess(menuAccess[item.id])
  );

  if (accessibleItems.length === 0) return null;

    return (
      <NavGroup collapsed={collapsed}>
        <NavItem 
          onClick={() => toggleGroup(groupKey)} 
          collapsed={collapsed} 
          style={{
            justifyContent: "space-between",
            // background: expandedGroups[groupKey] ? 'linear-gradient(135deg, rgba(0, 102, 255, 0.1), rgba(124, 58, 237, 0.06))' : 'rgba(0, 102, 255, 0.03)',
            fontWeight: '500',
            marginBottom: '0.4rem',
            padding: collapsed ? '0.5rem 0' : '0.5rem 0.6rem',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <NavIcon collapsed={collapsed} style={{ 
              // background: expandedGroups[groupKey] ? 'linear-gradient(135deg, rgba(0, 102, 255, 0.15), rgba(124, 58, 237, 0.1))' : 'rgba(0, 102, 255, 0.06)',
              width: '22px',
              height: '22px',
              minWidth: '22px',
              marginRight: '5px'
            }}>
              {expandedGroups[groupKey] ? <Minus size={16} strokeWidth={2.5} /> : <Plus size={16} strokeWidth={2.5} />}
            </NavIcon>
            <NavText collapsed={collapsed} style={{ 
              color: expandedGroups[groupKey] ? 'var(--primary-600)' : 'var(--neutral-500)', 
              fontWeight: 'var(--font-medium)',
              fontSize: 'var(--text-sm)'
            }}>
              {title}
            </NavText>
          </div>
        </NavItem>
        <motion.div
          initial={false}
          animate={{
            height: expandedGroups[groupKey] ? 'auto' : 0,
            opacity: expandedGroups[groupKey] ? 1 : 0
          }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }}
          style={{ overflow: 'hidden' }}
        >
          {renderNavItems(items, true)}
        </motion.div>
      </NavGroup>
    );
  };
  
  return (
    <SidebarContainer collapsed={collapsed}>
      {/* <BrandSection collapsed={collapsed}>
        <CloseIcon 
          size={24} 
          onClick={onClose}
          style={{ cursor: 'pointer', color: 'var(--neutral-500)', marginLeft:'8px' }}
        />
        <BrandIcon 
          src={`${basePath}/images/logo/meclaims-icon.png`}
          alt="BlueMorMon AI Icon" 
        />
        <BrandTextContainer collapsed={collapsed}>
          {/* <BrandName>BlueMorMon.ai</BrandName>
          <BrandTagline>The Agentic Platform</BrandTagline> */}
        {/* </BrandTextContainer> */}
        
      {/* </BrandSection> */}
      
      <MenuSection collapsed={collapsed}>
        <NavGroup>
          {renderNavItems(mainNavItems)}
        </NavGroup>
        
        {renderGroupWithToggle('Work Stream', agentWorkspaceItems, 'agentWorkspace')}
        {renderGroupWithToggle('Agent Ecosystem', agentEcosystemItems, 'agentEcosystem')}
        {renderGroupWithToggle('Knowledge & Flows', knowledgeItems, 'knowledge')}
        {renderGroupWithToggle('Governance', governanceItems, 'governance')}
        {renderGroupWithToggle('Evaluations', evalItems, 'evalWorkspace')}
        {/* {systemItems.length > 0 && renderGroupWithToggle('System', systemItems, 'system')} */}
      </MenuSection>
      
      <SettingsFooter collapsed={collapsed} onClick={() => navigate('/settings')}>
        <SettingsContent>
          <SettingsIcon>
            <Settings size={20} strokeWidth={2} />
          </SettingsIcon>
          <SettingsLabel collapsed={collapsed}>Settings</SettingsLabel>
        </SettingsContent>
      </SettingsFooter>
    </SidebarContainer>
  );
};

export default Sidebar;
